import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

const translations = {
  en: {
    brand: "Kavioura",
    tagline: "The Nordic equestrian marketplace",
    hero_title: "Where horses, riders and stables meet.",
    hero_sub: "Buy and sell horses, hire skilled workers, find services and stables across the Nordics.",
    get_started: "Get started",
    browse: "Browse marketplace",
    nav_horses: "Horses",
    nav_jobs: "Jobs",
    nav_services: "Services",
    nav_messages: "Messages",
    nav_dashboard: "Dashboard",
    login: "Log in",
    signup: "Sign up",
    logout: "Log out",
    email: "Email",
    password: "Password",
    name: "Full name",
    location: "Location",
    role: "I am a...",
    role_owner: "Horse owner",
    role_worker: "Worker",
    role_stable: "Stable",
    role_service_provider: "Service provider",
    role_seller: "Seller",
    role_buyer: "Buyer",
    create_account: "Create account",
    have_account: "Already have an account?",
    no_account: "No account yet?",
    search_placeholder: "Search horses, jobs, services...",
    filter_location: "Location",
    all: "All",
    horses: "Horses",
    jobs: "Jobs",
    services: "Services",
    tack: "Tack",
    boarding: "Boarding",
    type_label_horse: "Horses",
    type_label_job: "Jobs",
    type_label_service: "Services",
    type_label_tack: "Tack",
    type_label_boarding: "Boarding",
    type_label_rental: "Renting & Leasing",
    // Rentals & leasing
    nav_rentals: "Rentals",
    rentals: "Rentals & Leasing",
    rental_singular: "listing",
    rental_plural: "listings",
    rental_type: "Rental type",
    rental_weekly: "Weekly rental",
    rental_half_lease: "Half-lease",
    rental_full_lease: "Full lease",
    rental_care_share: "Care share",
    rental_other: "Other",
    rental_market_tagline: "Rentals & leasing",
    rental_market_title: "Rent or lease a horse.",
    rental_market_sub: "Weekly rides, half-lease and full lease horses posted by owners across the Nordics. Filter by location, category, gender and lease type.",
    rental_search_placeholder: "Search title, description...",
    no_rentals_match: "No rentals match your filters. Try loosening a few.",
    tack_type: "Tack type",
    tack_saddle: "Saddle",
    tack_bridle: "Bridle",
    tack_halter: "Halter",
    tack_blanket: "Blanket",
    tack_boots: "Boots",
    tack_other: "Other",
    condition: "Condition",
    condition_new: "New",
    condition_like_new: "Like new",
    condition_good: "Good",
    condition_used: "Used",
    size: "Size",
    boarding_type: "Boarding type",
    boarding_full_stall: "Full stall",
    boarding_paddock: "Paddock",
    boarding_pasture: "Pasture",
    boarding_self_care: "Self-care",
    capacity: "Capacity",
    amenities: "Amenities",
    horses_capacity: "horses",
    marketplace_label: "Marketplace",
    get_in_touch: "Get in touch",
    leave_feedback: "Leave feedback",
    feedback_sub: "Tell us what to fix, add, or improve.",
    feedback_category: "Category",
    fb_feedback: "Feedback",
    fb_bug: "Bug",
    fb_help: "Help",
    fb_other: "Other",
    optional: "optional",
    message: "Message",
    feedback_placeholder: "What's on your mind?",
    send_feedback: "Send",
    feedback_thanks: "Thanks — we'll be in touch.",
    urgent_help: "Urgent help / customer service",
    response_time: "We reply within one business day.",
    report_issue: "Report an issue",
    admin: "Admin",
    feedback_inbox: "Feedback inbox",
    total: "Total",
    unresolved: "Unresolved",
    show_resolved: "Show resolved",
    message_sent: "Message sent",
    private_message: "Private message",
    message_to: "Message to",
    message_user: "Send message",
    private_note: "Only you two can see this conversation.",
    notifications: "Notifications",
    no_notifications: "No notifications yet.",
    example_badge: "Example",
    example_note: "This is a Kavioura example listing shown to demonstrate what real sellers post here. It's not for sale — real listings from real users appear alongside it.",
    rating_legend: "1 = very bad · 3 = good · 5 = excellent",
    photo_updated: "Profile photo updated",
    upload_photo: "Upload photo",
    boost: "Boost",
    boosted: "Boosted",
    boosted_msg: "Boosted — will show first for 24 hours",
    boost_urgent: "Boost — appear first (need urgently)",
    replace_photo: "Replace photo",
    remove_photo: "Remove photo",
    photo_removed: "Profile photo removed",
    confirm_remove_photo: "Remove your profile photo?",
    crop_photo: "Photo",
    crop_preview: "Crop preview",
    zoom: "Zoom",
    use_photo: "Use this photo",
    mark_all_read: "Mark all read",
    all_read: "All marked as read",
    no_feedback: "No feedback matches your filters.",
    marked_resolved: "Marked as resolved",
    marked_open: "Reopened",
    mark_resolved: "Mark resolved",
    reopen: "Reopen",
    resolved: "Resolved",
    deleted: "Deleted",
    confirm_delete_feedback: "Delete this feedback permanently?",
    create_listing: "New listing",
    listing_type: "Listing type",
    title: "Title",
    description: "Description",
    price: "Price",
    currency: "Currency",
    breed: "Breed",
    age: "Age",
    gender: "Gender",
    discipline: "Discipline",
    category: "Category",
    photos: "Photos",
    upload_photos: "Upload photos",
    contact_email: "Contact email",
    contact_phone: "Contact phone",
    publish: "Publish listing",
    contact_seller: "Contact seller",
    send_message: "Send message",
    write_message: "Write a message...",
    posted_by: "Posted by",
    delete: "Delete",
    my_listings: "My listings",
    welcome_back: "Welcome back",
    thread_empty: "No conversations yet",
    listing_created: "Listing published",
    listing_deleted: "Listing removed",
    error_generic: "Something went wrong",
    inbox: "Inbox",
    footer_note: "Crafted for the Nordic equestrian community.",
    favorites: "Favorites",
    saved_listings: "Saved listings",
    no_favorites: "No favorites yet. Browse the marketplace and tap the heart to save listings.",
    edit_listing: "Edit listing",
    save_changes: "Save changes",
    listing_updated: "Listing updated",
    listings_by: "Listings by",
    listing: "listing",
    listings: "listings",
    no_public_listings: "This user hasn't published any listings yet.",
    bookings: "Bookings",
    my_bookings: "My bookings",
    provider_bookings_title: "Manage your availability",
    provider_bookings_sub: "Publish time slots and review incoming requests from clients.",
    add_slot: "Add availability",
    date: "Date",
    start_time: "Start",
    end_time: "End",
    slot_title_label: "Title (optional)",
    notes: "Notes",
    my_slots: "My slots",
    no_slots: "No availability yet — add a slot above.",
    pending_requests: "Pending requests",
    no_pending: "No pending requests.",
    history: "History",
    accept: "Accept",
    decline: "Decline",
    cancel: "Cancel",
    slot_added: "Slot added",
    slot_removed: "Slot removed",
    booking_accepted: "Booking accepted",
    booking_declined: "Booking declined",
    booking_cancelled: "Booking cancelled",
    booking_requested: "Booking request sent",
    no_bookings_client: "You haven't requested any bookings yet.",
    booking: "Booking",
    book_a_slot: "Book a slot",
    book_with: "Book with",
    pick_a_day: "Pick a day with availability from the calendar.",
    available_slots: "Available slots",
    message_optional: "Message (optional)",
    request_booking: "Request booking",
    prev: "Prev",
    next: "Next",
    reviews: "Reviews",
    review_singular: "review",
    reviews_plural: "reviews",
    no_reviews_yet: "No reviews yet.",
    reviews_inbox: "Reviews & feedback",
    reviews_inbox_sub: "You can leave a review after a completed booking or a closed deal.",
    pending_reviews: "Ready to review",
    no_pending_reviews: "Nothing to review right now. Reviews unlock after your booking ends or after you close a deal.",
    reviews_received: "Reviews received",
    no_reviews_received: "You have no reviews yet.",
    with: "with",
    leave_review: "Leave a review",
    review_booking: "Review booking",
    review_deal: "Review deal",
    review_of: "Review of",
    rating: "Rating",
    comment_optional: "Comment (optional)",
    review_placeholder: "Share how it went...",
    submit_review: "Submit review",
    review_submitted: "Review submitted",
    mark_as_sold: "Mark as sold",
    who_bought: "Who bought it?",
    search_buyer: "Search by name or email",
    no_users: "No users found.",
    deal_closed: "Deal closed",
    sold_label: "Sold",
    horse_market: "Horse Market",
    horse_market_tagline: "Dedicated horse marketplace",
    horse_market_title: "Find your next horse.",
    horse_market_sub: "Advanced filters — breed, discipline, gender, age and price — surface only the horses that match you.",
    horse_search_placeholder: "Search title, description...",
    filters: "Filters",
    clear: "Clear",
    hide_sold: "Hide sold horses",
    horse_singular: "horse",
    horse_plural: "horses",
    no_horses_match: "No horses match your filters. Try loosening a few.",
    hide_filters: "Hide filters",
    show_filters: "Show filters",
    sort_by: "Sort by",
    sort_newest: "Newest",
    sort_price_asc: "Price ↑ low to high",
    sort_price_desc: "Price ↓ high to low",
    sort_closest: "Closest to me",
    featured: "Featured listings",
    how_it_works: "How Kavioura works",
    step1_title: "Create your profile",
    step1_desc: "Register as owner, worker, stable, seller, buyer or service provider.",
    step2_title: "List or discover",
    step2_desc: "Post horses, jobs and services — or browse curated listings.",
    step3_title: "Connect directly",
    step3_desc: "Message people you trust and close the deal.",
    terms_title: "Terms & Conditions",
    terms_intro: "Please read and accept before creating your account.",
    terms_read_more: "Read the full terms",
    terms_hide: "Hide terms",
    terms_accept_label: "I have read and accept the Terms & Conditions.",
    terms_must_accept: "You must accept the Terms & Conditions to sign up.",
    terms_p1: "Kavioura is an online marketplace only — a platform that connects horse owners, workers, stables, service providers, sellers and buyers. Kavioura is not an employer, agent, broker, veterinarian, transporter or party to any deal made between users.",
    terms_p2: "Kavioura is not responsible for falsely described or misrepresented items, horses or services, nor for injuries, accidents, damages, illness or losses that occur during a job, transaction, ride, transport, boarding or any other interaction between users.",
    terms_p3: "Users are solely responsible for verifying the identity, qualifications, insurance, contracts and payment terms of the people they meet, hire or trade with on Kavioura. Always meet in person, inspect items and horses, and use written agreements when appropriate.",
    terms_p4: "If a listing, user or interaction seems fraudulent, abusive or unsafe, please report it to us immediately via the \"Leave feedback\" form in the footer so we can investigate and remove bad actors.",
    footer_terms: "Terms",
    privacy_policy: "Privacy",
    pp_overline: "Legal",
    pp_title: "Privacy Policy",
    pp_updated: "Last updated",
    pp_intro: "Kavioura respects your privacy. This policy explains what personal data we collect, why we collect it, and the rights you have under the EU General Data Protection Regulation (GDPR).",
    pp_who_title: "1. Who we are",
    pp_who_body: "Kavioura is an online marketplace for the Nordic equestrian community operated from Finland. For questions about your data or this policy, contact us at kaviourapalvelut@gmail.com.",
    pp_collect_title: "2. Data we collect",
    pp_collect_body: "Account data: name, email, password (stored hashed with bcrypt), role, optional location and bio, optional profile photo.\nListing data: text, price, photos and attributes you publish.\nInteraction data: messages, booking requests, reviews, favorites, feedback you send us.\nTechnical data: JWT session token in your browser's local storage, timestamps of account and terms acceptance.",
    pp_use_title: "3. How we use your data",
    pp_use_body: "To provide the marketplace (show your listings, deliver messages, run bookings and reviews), to keep the service secure and prevent fraud, to respond to your support requests, and to comply with our legal obligations.",
    pp_legal_title: "4. Legal basis",
    pp_legal_body: "We process your data under GDPR Article 6(1)(b) — performance of a contract (our Terms) — and Article 6(1)(f) — our legitimate interest in operating and securing a safe marketplace.",
    pp_sharing_title: "5. Sharing",
    pp_sharing_body: "We do not sell your data. Other Kavioura users can see the information you publish on your public profile and listings. Messages are visible only to you and the other party. We may share data with hosting providers who operate under GDPR-compatible contracts, and with authorities when required by law.",
    pp_retention_title: "6. How long we keep data",
    pp_retention_body: "We keep your account data for as long as your account exists. When you delete your account, we erase your profile, listings, messages, reviews, bookings, favorites and uploaded photos. Aggregated, non-identifiable analytics may be retained.",
    pp_rights_title: "7. Your rights",
    pp_rights_body: "You have the right to access, correct, export and erase your personal data, to object to or restrict processing, and to lodge a complaint with your national data protection authority (in Finland: tietosuoja.fi). You can delete your account and all associated data at any time from your profile page.",
    pp_cookies_title: "8. Cookies and local storage",
    pp_cookies_body: "Kavioura does not use tracking cookies. We store a session token and your language preference in your browser's local storage so you stay signed in and see the site in your language. These are cleared when you log out or delete your account.",
    pp_security_title: "9. Security",
    pp_security_body: "Passwords are stored hashed with bcrypt. Access to your account requires a signed session token. Please choose a strong password and log out on shared devices.",
    pp_contact_title: "10. Contact",
    pp_contact_body: "Data-protection requests and questions: kaviourapalvelut@gmail.com. We reply within one business day.",
    pp_contact_line: "Questions? Write to us:",
    cookie_title: "A quick note",
    cookie_body: "Kavioura uses your browser's local storage only to keep you signed in and remember your language. We don't use tracking cookies.",
    cookie_learn_more: "Read our Privacy Policy",
    cookie_accept: "Got it",
    danger_zone: "Danger zone",
    delete_account: "Delete account",
    delete_account_desc: "Permanently delete your Kavioura account and all data associated with it. This action cannot be undone.",
    delete_account_warning: "This will permanently erase your account. There is no way to recover it later.",
    delete_bullet_profile: "Your profile, name, email and photo",
    delete_bullet_listings: "All your listings and uploaded images",
    delete_bullet_messages: "All conversations you've had on Kavioura",
    delete_bullet_reviews: "Reviews you wrote and reviews about you",
    delete_bullet_bookings: "Booking history and availability slots",
    delete_confirm_label: "Type DELETE to confirm",
    delete_account_confirm: "Delete my account",
    account_deleted: "Your account has been deleted.",
    no_photo: "No photo",
    role_hint: "This is just a label so others know how you use Kavioura. It doesn't restrict what you can post or browse — anyone can list, buy, book or message regardless of role.",
    availability: "Availability",
    availability_title: "Availability calendar",
    availability_sub: "Click any day to see open time slots. Booked times are shown as unavailable — details are private between the two parties.",
    availability_sub_owner: "Click any day and add a slot. Others browsing your profile can book open slots directly. Booked details stay private.",
    open: "Open",
    open_short: "open",
    booked_short: "booked",
    pending_short: "pending",
    request_accepted: "Request accepted",
    request_declined: "Request declined",
    unknown: "Unknown",
    completion_confirmed: "Confirmed — you can now leave a review.",
    mark_deal_done: "Deal done · Confirm",
    write_review: "Write a review",
    you_reviewed: "You reviewed",
    review_completion: "Rate this person",
    edit_profile: "Edit profile",
    settings: "Settings",
    account: "Account",
    profile: "Profile",
    security: "Security",
    bio: "Bio",
    bio_placeholder: "Tell others a bit about yourself, your yard, your discipline…",
    security_hint: "For any change, enter your current password to confirm it's you.",
    current_password: "Current password",
    new_email: "New email",
    new_password: "New password",
    confirm_new_password: "Confirm new password",
    passwords_no_match: "New passwords don't match.",
    nothing_to_change: "Enter a new email or new password.",
    saved: "Saved.",
    my_profile: "My profile",
    view_my_profile: "View & edit my profile",
    booking_request: "Booking request",
    booking_requested: "Booking request sent",
    booking_notes_label: "Message to the provider (optional)",
    booking_notes_placeholder: "Anything they should know — your horse, location, needs…",
    booking_notes_hint: "The provider will see this note. Your name is shared only after they accept.",
    send_request: "Send request",
    login_required: "Please log in to book.",
    my_bookings_sub: "Every booking you're involved in — as a customer or as a provider. Pending requests you received show accept/decline buttons.",
    no_bookings_this_day: "Nothing scheduled on this day.",
    as_provider: "As provider",
    as_client: "As client",
    cancel_booking_q: "Cancel this booking?",
    status_pending: "Pending",
    status_accepted: "Accepted",
    status_declined: "Declined",
    status_cancelled: "Cancelled",
    boost_intro: "Boost your listing to the top of search & category pages.",
    boost_24h_label: "24 hours",
    boost_7d_label: "7 days",
    boost_24h_desc: "One day of premium placement",
    boost_7d_desc: "A full week of premium placement",
    proceed_to_payment: "Proceed to payment",
    stripe_powered: "Secure checkout by Stripe",
    payment_checking: "Confirming your payment…",
    payment_checking_sub: "This usually takes a few seconds. Don't close this page.",
    payment_success_title: "Payment successful — Boost active!",
    payment_success_sub: "Your listing is now boosted and appears at the top.",
    view_listing: "View listing",
    payment_pending_title: "Payment is still processing",
    payment_pending_sub: "It's taking longer than usual. Your boost will activate automatically when the payment clears.",
    payment_cancelled_title: "Payment cancelled",
    payment_cancelled_sub: "No charge was made. You can try again anytime.",
    back_to_dashboard: "Back to dashboard",
    today: "Today",
    no_slots_this_day: "No availability on this day. Pick another day or add a slot below.",
    add_slot_on_day: "Add a slot to this day",
    slot_title_placeholder: "e.g. Farrier visit, Vet check…",
    confirm_remove_slot: "Remove this slot?",
    time_end_after_start: "End time must be after start time.",
    your_booking: "Your booking",
    private_details: "Private",
    wd_mon: "Mon", wd_tue: "Tue", wd_wed: "Wed", wd_thu: "Thu", wd_fri: "Fri", wd_sat: "Sat", wd_sun: "Sun",
    refine_tack: "Refine tack",
    clear_filters: "Clear filters",
    any: "Any",
    size_pony: "Pony",
    size_cob: "Cob",
    size_full: "Full",
    size_xl: "XL",
    size_oversize: "Oversize",
    size_custom_placeholder: "or type e.g. 17.5\", 155cm, 135cm…",
    no_listings_match: "No listings match your filters.",
    horse_category: "Category",
    hc_pony: "Pony",
    hc_horse: "Horse",
    hc_foal: "Foal",
    hc_yearling: "Yearling",
  },
  fi: {
    brand: "Kavioura",
    tagline: "Pohjoismainen hevosmarkkinapaikka",
    hero_title: "Missä hevoset, ratsastajat ja tallit kohtaavat.",
    hero_sub: "Osta ja myy hevosia, palkkaa taitavia työntekijöitä ja löydä palveluita Pohjoismaissa.",
    get_started: "Aloita",
    browse: "Selaa markkinapaikkaa",
    nav_horses: "Hevoset",
    nav_jobs: "Työpaikat",
    nav_services: "Palvelut",
    nav_messages: "Viestit",
    nav_dashboard: "Kojelauta",
    login: "Kirjaudu",
    signup: "Rekisteröidy",
    logout: "Kirjaudu ulos",
    email: "Sähköposti",
    password: "Salasana",
    name: "Koko nimi",
    location: "Sijainti",
    role: "Olen...",
    role_owner: "Hevosen omistaja",
    role_worker: "Työntekijä",
    role_stable: "Talli",
    role_service_provider: "Palveluntarjoaja",
    role_seller: "Myyjä",
    role_buyer: "Ostaja",
    create_account: "Luo tili",
    have_account: "Onko sinulla jo tili?",
    no_account: "Ei vielä tiliä?",
    search_placeholder: "Etsi hevosia, työpaikkoja, palveluita...",
    filter_location: "Sijainti",
    all: "Kaikki",
    horses: "Hevoset",
    jobs: "Työpaikat",
    services: "Palvelut",
    tack: "Varusteet",
    boarding: "Talli / Tarhat",
    type_label_horse: "Hevoset",
    type_label_job: "Työt",
    type_label_service: "Palvelut",
    type_label_tack: "Varusteet",
    type_label_boarding: "Talli",
    type_label_rental: "Vuokraus & ylläpito",
    // Rentals & leasing (Vuokraus ja ylläpito)
    nav_rentals: "Vuokraus",
    rentals: "Vuokraus ja ylläpito",
    rental_singular: "ilmoitus",
    rental_plural: "ilmoitusta",
    rental_type: "Vuokraustyyppi",
    rental_weekly: "Viikkovuokraus",
    rental_half_lease: "Puolihoitosopimus",
    rental_full_lease: "Kokohoitosopimus",
    rental_care_share: "Ylläpitosopimus",
    rental_other: "Muu",
    rental_market_tagline: "Vuokraus & ylläpito",
    rental_market_title: "Vuokraa tai leasaa hevonen.",
    rental_market_sub: "Viikkovuokrauksia, puoli- ja kokohoitohevosia sekä ylläpitopaikkoja omistajilta ympäri Pohjoismaita. Suodata sijainnin, kategorian, sukupuolen ja sopimustyypin mukaan.",
    rental_search_placeholder: "Etsi otsikosta tai kuvauksesta...",
    no_rentals_match: "Ei suodattimiin sopivia ilmoituksia. Kokeile löyhempiä suodattimia.",
    tack_type: "Varustetyyppi",
    tack_saddle: "Satula",
    tack_bridle: "Suitset",
    tack_halter: "Riimu",
    tack_blanket: "Loimi",
    tack_boots: "Suojat",
    tack_other: "Muu",
    condition: "Kunto",
    condition_new: "Uusi",
    condition_like_new: "Kuin uusi",
    condition_good: "Hyvä",
    condition_used: "Käytetty",
    size: "Koko",
    boarding_type: "Tallipaikan tyyppi",
    boarding_full_stall: "Karsina",
    boarding_paddock: "Tarha",
    boarding_pasture: "Laidun",
    boarding_self_care: "Osahoito",
    capacity: "Kapasiteetti",
    amenities: "Palvelut",
    horses_capacity: "hevosta",
    marketplace_label: "Markkinapaikka",
    get_in_touch: "Ota yhteyttä",
    leave_feedback: "Jätä palautetta",
    feedback_sub: "Kerro mitä korjaamme, lisäämme tai parannamme.",
    feedback_category: "Kategoria",
    fb_feedback: "Palaute",
    fb_bug: "Vikailmoitus",
    fb_help: "Apua",
    fb_other: "Muu",
    optional: "valinnainen",
    message: "Viesti",
    feedback_placeholder: "Mitä mielessäsi?",
    send_feedback: "Lähetä",
    feedback_thanks: "Kiitos — otamme yhteyttä.",
    urgent_help: "Kiireellinen apu / asiakaspalvelu",
    response_time: "Vastaamme yhden arkipäivän kuluessa.",
    report_issue: "Ilmoita ongelmasta",
    create_listing: "Uusi ilmoitus",
    listing_type: "Ilmoituksen tyyppi",
    title: "Otsikko",
    description: "Kuvaus",
    price: "Hinta",
    currency: "Valuutta",
    breed: "Rotu",
    age: "Ikä",
    gender: "Sukupuoli",
    discipline: "Laji",
    category: "Kategoria",
    photos: "Kuvat",
    upload_photos: "Lataa kuvia",
    contact_email: "Sähköposti",
    contact_phone: "Puhelin",
    publish: "Julkaise ilmoitus",
    contact_seller: "Ota yhteyttä myyjään",
    send_message: "Lähetä viesti",
    write_message: "Kirjoita viesti...",
    posted_by: "Julkaisija",
    delete: "Poista",
    my_listings: "Omat ilmoitukset",
    welcome_back: "Tervetuloa takaisin",
    thread_empty: "Ei keskusteluja vielä",
    listing_created: "Ilmoitus julkaistu",
    listing_deleted: "Ilmoitus poistettu",
    error_generic: "Jotain meni pieleen",
    inbox: "Postilaatikko",
    footer_note: "Tehty pohjoismaiselle hevosyhteisölle.",
    favorites: "Suosikit",
    saved_listings: "Tallennetut ilmoitukset",
    no_favorites: "Ei suosikkeja vielä. Selaa markkinapaikkaa ja tallenna ilmoituksia painamalla sydäntä.",
    edit_listing: "Muokkaa ilmoitusta",
    save_changes: "Tallenna muutokset",
    listing_updated: "Ilmoitus päivitetty",
    listings_by: "Ilmoitukset käyttäjältä",
    listing: "ilmoitus",
    listings: "ilmoitusta",
    no_public_listings: "Tämä käyttäjä ei ole vielä julkaissut ilmoituksia.",
    featured: "Nostetut ilmoitukset",
    how_it_works: "Näin Kavioura toimii",
    step1_title: "Luo profiilisi",
    step1_desc: "Rekisteröidy omistajana, työntekijänä, tallina, myyjänä, ostajana tai palveluntarjoajana.",
    step2_title: "Ilmoita tai löydä",
    step2_desc: "Julkaise hevosia, työpaikkoja ja palveluita — tai selaa ilmoituksia.",
    step3_title: "Ota suoraan yhteyttä",
    step3_desc: "Viestittele ihmisten kanssa ja tee kauppaa.",
    reviews: "Arvostelut",
    review_singular: "arvostelu",
    reviews_plural: "arvostelua",
    no_reviews_yet: "Ei vielä arvosteluja.",
    reviews_inbox: "Arvostelut",
    reviews_inbox_sub: "Voit arvostella päättyneen varauksen tai kaupan jälkeen.",
    pending_reviews: "Voit arvostella",
    no_pending_reviews: "Ei arvosteltavaa nyt.",
    reviews_received: "Saadut arvostelut",
    no_reviews_received: "Ei arvosteluja.",
    with: "kohteelle",
    leave_review: "Jätä arvostelu",
    review_booking: "Arvostele varaus",
    review_deal: "Arvostele kauppa",
    review_of: "Arvostelu käyttäjälle",
    rating: "Arvosana",
    comment_optional: "Kommentti (valinnainen)",
    review_placeholder: "Kerro miten meni...",
    submit_review: "Lähetä arvostelu",
    review_submitted: "Arvostelu lähetetty",
    mark_as_sold: "Merkitse myydyksi",
    who_bought: "Kuka osti?",
    search_buyer: "Etsi nimellä tai sähköpostilla",
    no_users: "Käyttäjiä ei löytynyt.",
    deal_closed: "Kauppa suljettu",
    sold_label: "Myyty",
    horse_market: "Hevoskauppa",
    horse_market_tagline: "Erillinen hevosmarkkinapaikka",
    horse_market_title: "Löydä seuraava hevosesi.",
    horse_market_sub: "Kehittyneet suodattimet — rotu, laji, sukupuoli, ikä ja hinta — näyttävät vain sinulle sopivat hevoset.",
    horse_search_placeholder: "Etsi otsikosta tai kuvauksesta...",
    filters: "Suodattimet",
    clear: "Tyhjennä",
    hide_sold: "Piilota myydyt",
    horse_singular: "hevonen",
    horse_plural: "hevosta",
    no_horses_match: "Ei osumia. Kokeile löyhempiä suodattimia.",
    hide_filters: "Piilota suodattimet",
    show_filters: "Näytä suodattimet",
    sort_by: "Järjestä",
    sort_newest: "Uusimmat",
    sort_price_asc: "Hinta ↑ nouseva",
    sort_price_desc: "Hinta ↓ laskeva",
    sort_closest: "Lähimpänä minua",
    terms_title: "Käyttöehdot",
    terms_intro: "Lue ja hyväksy ehdot ennen tilin luomista.",
    terms_read_more: "Lue koko teksti",
    terms_hide: "Piilota ehdot",
    terms_accept_label: "Olen lukenut ja hyväksyn käyttöehdot.",
    terms_must_accept: "Sinun on hyväksyttävä käyttöehdot rekisteröityäksesi.",
    terms_p1: "Kavioura on vain verkkomarkkinapaikka — alusta, joka yhdistää hevosten omistajat, työntekijät, tallit, palveluntarjoajat, myyjät ja ostajat. Kavioura ei ole työnantaja, edustaja, välittäjä, eläinlääkäri, kuljettaja eikä osapuoli käyttäjien välisissä kaupoissa.",
    terms_p2: "Kavioura ei vastaa väärin kuvatuista tai harhaanjohtavasti myydyistä tuotteista, hevosista tai palveluista eikä loukkaantumisista, onnettomuuksista, vahingoista, sairauksista tai menetyksistä, jotka tapahtuvat työn, kaupan, ratsastuksen, kuljetuksen, tallipaikan tai muun käyttäjien välisen vuorovaikutuksen aikana.",
    terms_p3: "Käyttäjät ovat yksin vastuussa tapaamiensa, palkkaamiensa tai kaupankäynnissä olevien henkilöiden henkilöllisyyden, pätevyyden, vakuutusten, sopimusten ja maksuehtojen tarkistamisesta. Tapaa aina henkilökohtaisesti, tarkista tuotteet ja hevoset ja käytä kirjallisia sopimuksia tarvittaessa.",
    terms_p4: "Jos ilmoitus, käyttäjä tai vuorovaikutus vaikuttaa petolliselta, epäasialliselta tai vaaralliselta, ilmoita siitä meille välittömästi alatunnisteen \"Jätä palautetta\" -lomakkeella, jotta voimme tutkia ja poistaa väärinkäyttäjät.",
    footer_terms: "Käyttöehdot",
    privacy_policy: "Tietosuoja",
    pp_overline: "Lakitiedot",
    pp_title: "Tietosuojaseloste",
    pp_updated: "Viimeksi päivitetty",
    pp_intro: "Kavioura kunnioittaa yksityisyyttäsi. Tämä seloste kertoo, mitä henkilötietoja keräämme, miksi keräämme niitä ja mitä oikeuksia sinulla on EU:n yleisen tietosuoja-asetuksen (GDPR) mukaan.",
    pp_who_title: "1. Kuka olemme",
    pp_who_body: "Kavioura on suomalainen pohjoismaisen hevosyhteisön verkkomarkkinapaikka. Tietoja koskevissa kysymyksissä ota yhteyttä: kaviourapalvelut@gmail.com.",
    pp_collect_title: "2. Kerättävät tiedot",
    pp_collect_body: "Tilitiedot: nimi, sähköposti, salasana (bcrypt-hashattuna), rooli, valinnainen sijainti ja esittely, valinnainen profiilikuva.\nIlmoitusten tiedot: julkaisemasi teksti, hinta, kuvat ja ominaisuudet.\nVuorovaikutustiedot: viestit, varauspyynnöt, arvostelut, suosikit ja meille lähettämäsi palaute.\nTekniset tiedot: JWT-istuntotunniste selaimen local storagessa sekä tilin ja käyttöehtojen hyväksymisajankohdat.",
    pp_use_title: "3. Miksi käsittelemme tietoja",
    pp_use_body: "Palvelun tarjoamiseen (ilmoitukset, viestit, varaukset, arvostelut), palvelun tietoturvasta ja väärinkäytösten estämisestä huolehtimiseen, tukipyyntöihin vastaamiseen ja lakisääteisten velvoitteiden täyttämiseen.",
    pp_legal_title: "4. Oikeusperuste",
    pp_legal_body: "Käsittelemme tietoja GDPR-artiklojen 6(1)(b) — sopimuksen täyttäminen (käyttöehdot) — ja 6(1)(f) — oikeutettu etu turvallisen markkinapaikan ylläpitämisessä — perusteella.",
    pp_sharing_title: "5. Tietojen jakaminen",
    pp_sharing_body: "Emme myy tietojasi. Muut Kavioura-käyttäjät näkevät julkisesti julkaisemasi profiilin ja ilmoitukset. Viestit näkyvät vain sinulle ja vastaanottajalle. Voimme jakaa tietoja isännöintipalveluiden kanssa GDPR-yhteensopivilla sopimuksilla ja viranomaisille lain vaatiessa.",
    pp_retention_title: "6. Tietojen säilytys",
    pp_retention_body: "Säilytämme tilisi tietoja niin kauan kuin tilisi on olemassa. Kun poistat tilisi, poistamme profiilisi, ilmoituksesi, viestisi, arvostelusi, varauksesi, suosikkisi ja lataamasi kuvat. Anonymisoituja analytiikkatietoja voidaan säilyttää.",
    pp_rights_title: "7. Oikeutesi",
    pp_rights_body: "Sinulla on oikeus tarkastaa, korjata, siirtää ja poistaa henkilötietosi, vastustaa tai rajoittaa käsittelyä sekä tehdä valitus tietosuojaviranomaiselle (Suomessa: tietosuoja.fi). Voit poistaa tilisi ja kaikki siihen liittyvät tiedot milloin tahansa profiilisivultasi.",
    pp_cookies_title: "8. Evästeet ja local storage",
    pp_cookies_body: "Kavioura ei käytä seurantaevästeitä. Tallennamme selaimesi local storageen istuntotunnisteen ja kielivalinnan, jotta pysyt kirjautuneena ja näet sivuston omalla kielelläsi. Nämä poistetaan uloskirjautumisen tai tilin poiston yhteydessä.",
    pp_security_title: "9. Tietoturva",
    pp_security_body: "Salasanat tallennetaan bcrypt-hashattuina. Tiliisi pääsy edellyttää allekirjoitettua istuntotunnistetta. Valitse vahva salasana ja kirjaudu ulos jaetuista laitteista.",
    pp_contact_title: "10. Yhteydenotto",
    pp_contact_body: "Tietosuojaan liittyvät pyynnöt ja kysymykset: kaviourapalvelut@gmail.com. Vastaamme yhden arkipäivän kuluessa.",
    pp_contact_line: "Kysyttävää? Kirjoita meille:",
    cookie_title: "Lyhyt huomautus",
    cookie_body: "Kavioura käyttää selaimesi local storagea vain pitääkseen sinut kirjautuneena ja muistaakseen kielivalintasi. Emme käytä seurantaevästeitä.",
    cookie_learn_more: "Lue tietosuojaseloste",
    cookie_accept: "Selvä",
    danger_zone: "Vaaravyöhyke",
    delete_account: "Poista tili",
    delete_account_desc: "Poista Kavioura-tilisi ja kaikki siihen liittyvät tiedot pysyvästi. Tätä toimintoa ei voi kumota.",
    delete_account_warning: "Tämä poistaa tilisi pysyvästi. Sitä ei voi enää palauttaa.",
    delete_bullet_profile: "Profiilisi, nimi, sähköposti ja kuva",
    delete_bullet_listings: "Kaikki ilmoituksesi ja ladatut kuvat",
    delete_bullet_messages: "Kaikki Kaviourassa käymäsi keskustelut",
    delete_bullet_reviews: "Kirjoittamasi ja saamasi arvostelut",
    delete_bullet_bookings: "Varaushistoria ja lisätyt vapaat ajat",
    delete_confirm_label: "Kirjoita DELETE vahvistaaksesi",
    delete_account_confirm: "Poista tilini",
    account_deleted: "Tilisi on poistettu.",
    no_photo: "Ei kuvaa",
    role_hint: "Tämä on vain merkintä, jotta muut näkevät miten käytät Kavioura-sivustoa. Se ei rajoita mitä voit julkaista tai selata — kuka tahansa voi ilmoittaa, ostaa, varata tai viestiä roolista riippumatta.",
    availability: "Vapaat ajat",
    availability_title: "Kalenteri",
    availability_sub: "Klikkaa mitä tahansa päivää nähdäksesi vapaat ajat. Varatut ajat näkyvät suljettuina — yksityiskohdat vain asianosaisille.",
    availability_sub_owner: "Klikkaa haluamaasi päivää ja lisää vapaa aika. Muut näkevät ne ja voivat varata suoraan. Varausten yksityiskohdat pysyvät yksityisinä.",
    open: "Vapaa",
    open_short: "vapaa",
    booked_short: "varattu",
    pending_short: "odottaa",
    pending_requests: "Odottavat pyynnöt",
    accept: "Hyväksy",
    decline: "Hylkää",
    request_accepted: "Pyyntö hyväksytty",
    request_declined: "Pyyntö hylätty",
    unknown: "Tuntematon",
    completion_confirmed: "Vahvistettu — voit nyt jättää arvostelun.",
    mark_deal_done: "Kaupat tehty · Vahvista",
    write_review: "Kirjoita arvostelu",
    you_reviewed: "Olet arvostellut",
    review_completion: "Anna arvostelu",
    edit_profile: "Muokkaa profiilia",
    settings: "Asetukset",
    account: "Tili",
    profile: "Profiili",
    security: "Turvallisuus",
    bio: "Kuvaus",
    bio_placeholder: "Kerro itsestäsi, tallistasi, lajistasi…",
    security_hint: "Vahvista muutokset syöttämällä nykyinen salasanasi.",
    current_password: "Nykyinen salasana",
    new_email: "Uusi sähköposti",
    new_password: "Uusi salasana",
    confirm_new_password: "Vahvista uusi salasana",
    passwords_no_match: "Uudet salasanat eivät täsmää.",
    nothing_to_change: "Syötä uusi sähköposti tai uusi salasana.",
    saved: "Tallennettu.",
    my_profile: "Oma profiili",
    view_my_profile: "Näytä ja muokkaa profiilia",
    booking_request: "Varauspyyntö",
    booking_requested: "Varauspyyntö lähetetty",
    booking_notes_label: "Viesti palveluntarjoajalle (valinnainen)",
    booking_notes_placeholder: "Kerro tarpeistasi — hevonen, sijainti, erityistoiveet…",
    booking_notes_hint: "Palveluntarjoaja näkee tämän viestin. Nimesi jaetaan vasta hyväksymisen jälkeen.",
    send_request: "Lähetä pyyntö",
    login_required: "Kirjaudu sisään varataksesi.",
    my_bookings_sub: "Kaikki varaukset joissa olet mukana — asiakkaana tai palveluntarjoajana. Saamasi odottavat pyynnöt näyttävät hyväksy/hylkää-napit.",
    no_bookings_this_day: "Ei varauksia tänä päivänä.",
    as_provider: "Tarjoajana",
    as_client: "Asiakkaana",
    cancel_booking_q: "Peruutetaanko tämä varaus?",
    status_pending: "Odottaa",
    status_accepted: "Hyväksytty",
    status_declined: "Hylätty",
    status_cancelled: "Peruttu",
    boost_intro: "Nosta ilmoituksesi hakutulosten ja kategoriasivujen kärkeen.",
    boost_24h_label: "24 tuntia",
    boost_7d_label: "7 päivää",
    boost_24h_desc: "Yksi päivä etusijalla",
    boost_7d_desc: "Kokonainen viikko etusijalla",
    proceed_to_payment: "Siirry maksamaan",
    stripe_powered: "Suojattu maksu Stripella",
    payment_checking: "Vahvistetaan maksua…",
    payment_checking_sub: "Kestää yleensä muutaman sekunnin. Älä sulje sivua.",
    payment_success_title: "Maksu onnistui — Boost aktiivinen!",
    payment_success_sub: "Ilmoituksesi on nyt boostattu ja nousee kärkeen.",
    view_listing: "Katso ilmoitus",
    payment_pending_title: "Maksu vielä käsittelyssä",
    payment_pending_sub: "Kestää tavanomaista pidempään. Boost aktivoituu automaattisesti kun maksu selviää.",
    payment_cancelled_title: "Maksu peruttu",
    payment_cancelled_sub: "Sinulta ei veloitettu mitään. Voit yrittää uudelleen milloin tahansa.",
    back_to_dashboard: "Takaisin dashboardille",
    today: "Tänään",
    no_slots_this_day: "Ei vapaita aikoja tänä päivänä. Valitse toinen päivä tai lisää aika alta.",
    add_slot_on_day: "Lisää aika tälle päivälle",
    slot_title_placeholder: "esim. Kengitys, eläinlääkäri…",
    confirm_remove_slot: "Poistetaanko tämä aika?",
    time_end_after_start: "Lopetusajan on oltava aloitusajan jälkeen.",
    your_booking: "Sinun varauksesi",
    private_details: "Yksityinen",
    wd_mon: "Ma", wd_tue: "Ti", wd_wed: "Ke", wd_thu: "To", wd_fri: "Pe", wd_sat: "La", wd_sun: "Su",
    refine_tack: "Rajaa tarvikkeita",
    clear_filters: "Tyhjennä suodattimet",
    any: "Kaikki",
    size_pony: "Pony",
    size_cob: "Cob",
    size_full: "Full",
    size_xl: "XL",
    size_oversize: "Yli-koko",
    size_custom_placeholder: "tai kirjoita esim. 17.5\", 155cm, 135cm…",
    no_listings_match: "Ei suodattimiin sopivia ilmoituksia.",
    horse_category: "Kategoria",
    hc_pony: "Poni",
    hc_horse: "Hevonen",
    hc_foal: "Varsa",
    hc_yearling: "1-vuotias",
  },
  sv: {
    brand: "Kavioura",
    tagline: "Den nordiska hästmarknaden",
    hero_title: "Där hästar, ryttare och stall möts.",
    hero_sub: "Köp och sälj hästar, anställ skickliga medarbetare, hitta tjänster och stall i Norden.",
    get_started: "Kom igång",
    browse: "Bläddra på marknaden",
    nav_horses: "Hästar",
    nav_jobs: "Jobb",
    nav_services: "Tjänster",
    nav_messages: "Meddelanden",
    nav_dashboard: "Kontrollpanel",
    login: "Logga in",
    signup: "Skapa konto",
    logout: "Logga ut",
    email: "E-post",
    password: "Lösenord",
    name: "Fullständigt namn",
    location: "Plats",
    role: "Jag är...",
    role_owner: "Hästägare",
    role_worker: "Medarbetare",
    role_stable: "Stall",
    role_service_provider: "Tjänsteleverantör",
    role_seller: "Säljare",
    role_buyer: "Köpare",
    create_account: "Skapa konto",
    have_account: "Har du redan ett konto?",
    no_account: "Inget konto än?",
    search_placeholder: "Sök hästar, jobb, tjänster...",
    filter_location: "Plats",
    all: "Alla",
    horses: "Hästar",
    jobs: "Jobb",
    services: "Tjänster",
    tack: "Utrustning",
    boarding: "Stallplatser",
    type_label_horse: "Hästar",
    type_label_job: "Jobb",
    type_label_service: "Tjänster",
    type_label_tack: "Utrustning",
    type_label_boarding: "Stall",
    type_label_rental: "Uthyrning & leasing",
    // Rentals & leasing
    nav_rentals: "Uthyrning",
    rentals: "Uthyrning & leasing",
    rental_singular: "annons",
    rental_plural: "annonser",
    rental_type: "Uthyrningstyp",
    rental_weekly: "Veckouthyrning",
    rental_half_lease: "Delhyra",
    rental_full_lease: "Helhyra",
    rental_care_share: "Skötselavtal",
    rental_other: "Övrigt",
    rental_market_tagline: "Uthyrning & leasing",
    rental_market_title: "Hyr eller leasa en häst.",
    rental_market_sub: "Veckouthyrning, del- och helhyrhästar och skötselavtal från ägare i hela Norden. Filtrera på plats, kategori, kön och hyrestyp.",
    rental_search_placeholder: "Sök titel, beskrivning...",
    no_rentals_match: "Inga annonser matchar dina filter. Prova mildare filter.",
    tack_type: "Utrustningstyp",
    tack_saddle: "Sadel",
    tack_bridle: "Träns",
    tack_halter: "Grimma",
    tack_blanket: "Täcke",
    tack_boots: "Benskydd",
    tack_other: "Övrigt",
    condition: "Skick",
    condition_new: "Nytt",
    condition_like_new: "Som nytt",
    condition_good: "Bra",
    condition_used: "Använt",
    size: "Storlek",
    boarding_type: "Stallplatstyp",
    boarding_full_stall: "Box",
    boarding_paddock: "Hage",
    boarding_pasture: "Bete",
    boarding_self_care: "Egenskötsel",
    capacity: "Kapacitet",
    amenities: "Faciliteter",
    horses_capacity: "hästar",
    marketplace_label: "Marknad",
    get_in_touch: "Kontakta oss",
    leave_feedback: "Lämna feedback",
    feedback_sub: "Berätta vad vi ska fixa, lägga till eller förbättra.",
    feedback_category: "Kategori",
    fb_feedback: "Feedback",
    fb_bug: "Bugg",
    fb_help: "Hjälp",
    fb_other: "Övrigt",
    optional: "valfritt",
    message: "Meddelande",
    feedback_placeholder: "Vad tänker du?",
    send_feedback: "Skicka",
    feedback_thanks: "Tack — vi hör av oss.",
    urgent_help: "Akut hjälp / kundtjänst",
    response_time: "Vi svarar inom en arbetsdag.",
    report_issue: "Rapportera problem",
    create_listing: "Ny annons",
    listing_type: "Annonstyp",
    title: "Titel",
    description: "Beskrivning",
    price: "Pris",
    currency: "Valuta",
    breed: "Ras",
    age: "Ålder",
    gender: "Kön",
    discipline: "Disciplin",
    category: "Kategori",
    photos: "Foton",
    upload_photos: "Ladda upp foton",
    contact_email: "E-post",
    contact_phone: "Telefon",
    publish: "Publicera annons",
    contact_seller: "Kontakta säljaren",
    send_message: "Skicka meddelande",
    write_message: "Skriv ett meddelande...",
    posted_by: "Publicerad av",
    delete: "Ta bort",
    my_listings: "Mina annonser",
    welcome_back: "Välkommen tillbaka",
    thread_empty: "Inga konversationer ännu",
    listing_created: "Annons publicerad",
    listing_deleted: "Annons borttagen",
    error_generic: "Något gick fel",
    inbox: "Inkorg",
    footer_note: "Skapad för den nordiska hästgemenskapen.",
    favorites: "Favoriter",
    saved_listings: "Sparade annonser",
    no_favorites: "Inga favoriter ännu. Bläddra i marknaden och tryck på hjärtat för att spara annonser.",
    edit_listing: "Redigera annons",
    save_changes: "Spara ändringar",
    listing_updated: "Annons uppdaterad",
    listings_by: "Annonser av",
    listing: "annons",
    listings: "annonser",
    no_public_listings: "Denna användare har inte publicerat några annonser ännu.",
    bookings: "Bokningar",
    my_bookings: "Mina bokningar",
    provider_bookings_title: "Hantera din tillgänglighet",
    provider_bookings_sub: "Publicera lediga tider och granska förfrågningar.",
    add_slot: "Lägg till tid",
    date: "Datum",
    start_time: "Start",
    end_time: "Slut",
    slot_title_label: "Titel (valfri)",
    notes: "Anteckningar",
    my_slots: "Mina tider",
    no_slots: "Ingen tillgänglighet ännu — lägg till en tid ovan.",
    pending_requests: "Väntande förfrågningar",
    no_pending: "Inga väntande förfrågningar.",
    history: "Historik",
    accept: "Acceptera",
    decline: "Avböj",
    cancel: "Avbryt",
    slot_added: "Tid tillagd",
    slot_removed: "Tid borttagen",
    booking_accepted: "Bokning accepterad",
    booking_declined: "Bokning avböjd",
    booking_cancelled: "Bokning avbruten",
    booking_requested: "Bokningsförfrågan skickad",
    no_bookings_client: "Du har inte begärt några bokningar ännu.",
    booking: "Bokning",
    book_a_slot: "Boka en tid",
    book_with: "Boka hos",
    pick_a_day: "Välj en dag med lediga tider.",
    available_slots: "Lediga tider",
    message_optional: "Meddelande (valfritt)",
    request_booking: "Skicka förfrågan",
    prev: "Föreg.",
    next: "Nästa",
    reviews: "Recensioner",
    review_singular: "recension",
    reviews_plural: "recensioner",
    no_reviews_yet: "Inga recensioner ännu.",
    reviews_inbox: "Recensioner & feedback",
    reviews_inbox_sub: "Du kan lämna en recension efter en genomförd bokning eller en avslutad affär.",
    pending_reviews: "Redo att recensera",
    no_pending_reviews: "Inget att recensera just nu.",
    reviews_received: "Mottagna recensioner",
    no_reviews_received: "Du har inga recensioner ännu.",
    with: "med",
    leave_review: "Lämna en recension",
    review_booking: "Recensera bokning",
    review_deal: "Recensera affär",
    review_of: "Recension av",
    rating: "Betyg",
    comment_optional: "Kommentar (valfri)",
    review_placeholder: "Berätta hur det gick...",
    submit_review: "Skicka recension",
    review_submitted: "Recension skickad",
    mark_as_sold: "Markera som såld",
    who_bought: "Vem köpte?",
    search_buyer: "Sök på namn eller e-post",
    no_users: "Inga användare hittades.",
    deal_closed: "Affär avslutad",
    sold_label: "Såld",
    horse_market: "Hästmarknad",
    horse_market_tagline: "Dedikerad hästmarknad",
    horse_market_title: "Hitta din nästa häst.",
    horse_market_sub: "Avancerade filter — ras, disciplin, kön, ålder och pris — visar bara hästar som matchar dig.",
    horse_search_placeholder: "Sök titel, beskrivning...",
    filters: "Filter",
    clear: "Rensa",
    hide_sold: "Dölj sålda hästar",
    horse_singular: "häst",
    horse_plural: "hästar",
    no_horses_match: "Inga hästar matchar. Prova mildare filter.",
    hide_filters: "Dölj filter",
    show_filters: "Visa filter",
    sort_by: "Sortera efter",
    sort_newest: "Nyaste",
    sort_price_asc: "Pris ↑ lågt till högt",
    sort_price_desc: "Pris ↓ högt till lågt",
    sort_closest: "Närmast mig",
    featured: "Utvalda annonser",
    how_it_works: "Så fungerar Kavioura",
    step1_title: "Skapa din profil",
    step1_desc: "Registrera dig som ägare, medarbetare, stall, säljare, köpare eller tjänsteleverantör.",
    step2_title: "Annonsera eller upptäck",
    step2_desc: "Publicera hästar, jobb och tjänster — eller bläddra bland utvalda annonser.",
    step3_title: "Ta direktkontakt",
    step3_desc: "Skicka meddelande till personer du litar på och avsluta affären.",
    terms_title: "Villkor",
    terms_intro: "Läs och godkänn innan du skapar ditt konto.",
    terms_read_more: "Läs de fullständiga villkoren",
    terms_hide: "Dölj villkor",
    terms_accept_label: "Jag har läst och godkänner villkoren.",
    terms_must_accept: "Du måste godkänna villkoren för att kunna registrera dig.",
    terms_p1: "Kavioura är endast en online-marknadsplats — en plattform som kopplar samman hästägare, medarbetare, stall, tjänsteleverantörer, säljare och köpare. Kavioura är inte arbetsgivare, agent, mäklare, veterinär, transportör eller part i någon affär mellan användare.",
    terms_p2: "Kavioura ansvarar inte för felaktigt beskrivna eller vilseledande sålda föremål, hästar eller tjänster, och inte heller för skador, olyckor, sjukdomar eller förluster som uppstår under ett jobb, en affär, ridning, transport, stallplats eller annan interaktion mellan användare.",
    terms_p3: "Användare är ensamma ansvariga för att kontrollera identitet, kvalifikationer, försäkringar, avtal och betalningsvillkor för de personer de träffar, anställer eller handlar med på Kavioura. Träffas alltid personligen, inspektera föremål och hästar och använd skriftliga avtal när det är lämpligt.",
    terms_p4: "Om en annons, användare eller interaktion verkar bedräglig, kränkande eller osäker, rapportera det omedelbart till oss via formuläret \"Lämna feedback\" i sidfoten så att vi kan utreda och ta bort dåliga aktörer.",
    footer_terms: "Villkor",
    privacy_policy: "Integritet",
    pp_overline: "Juridiskt",
    pp_title: "Integritetspolicy",
    pp_updated: "Senast uppdaterad",
    pp_intro: "Kavioura respekterar din integritet. Denna policy förklarar vilka personuppgifter vi samlar in, varför vi samlar in dem och vilka rättigheter du har enligt EU:s dataskyddsförordning (GDPR).",
    pp_who_title: "1. Vilka vi är",
    pp_who_body: "Kavioura är en nordisk online-marknadsplats för hästintresserade, driven från Finland. För frågor om dina uppgifter, kontakta oss på kaviourapalvelut@gmail.com.",
    pp_collect_title: "2. Vilka uppgifter vi samlar in",
    pp_collect_body: "Kontouppgifter: namn, e-post, lösenord (lagras hashat med bcrypt), roll, valfri plats och biografi, valfri profilbild.\nAnnonsdata: text, pris, foton och egenskaper du publicerar.\nInteraktionsdata: meddelanden, bokningsförfrågningar, recensioner, favoriter och feedback du skickar oss.\nTeknisk data: JWT-sessionstoken i webbläsarens lokala lagring, tidsstämplar för konto- och villkorsgodkännande.",
    pp_use_title: "3. Hur vi använder uppgifterna",
    pp_use_body: "För att tillhandahålla marknadsplatsen (visa dina annonser, leverera meddelanden, hantera bokningar och recensioner), för att hålla tjänsten säker och förebygga bedrägeri, för att svara på supportförfrågningar och för att uppfylla lagkrav.",
    pp_legal_title: "4. Rättslig grund",
    pp_legal_body: "Vi behandlar dina uppgifter enligt GDPR artikel 6(1)(b) — fullgörande av avtal (våra villkor) — och artikel 6(1)(f) — vårt berättigade intresse av att driva en säker marknadsplats.",
    pp_sharing_title: "5. Delning",
    pp_sharing_body: "Vi säljer inte dina uppgifter. Andra Kavioura-användare kan se den information du publicerar på din offentliga profil och dina annonser. Meddelanden syns endast för dig och mottagaren. Vi kan dela data med värdleverantörer under GDPR-kompatibla avtal och med myndigheter när lagen kräver det.",
    pp_retention_title: "6. Hur länge vi sparar uppgifter",
    pp_retention_body: "Vi behåller ditt konto så länge det finns. När du raderar ditt konto tar vi bort din profil, annonser, meddelanden, recensioner, bokningar, favoriter och uppladdade bilder. Aggregerad, icke-identifierbar analys kan sparas.",
    pp_rights_title: "7. Dina rättigheter",
    pp_rights_body: "Du har rätt att få tillgång till, korrigera, exportera och radera dina personuppgifter, invända mot eller begränsa behandling samt inge klagomål till din nationella dataskyddsmyndighet (i Finland: tietosuoja.fi, i Sverige: imy.se). Du kan när som helst radera ditt konto och all tillhörande data från din profilsida.",
    pp_cookies_title: "8. Cookies och lokal lagring",
    pp_cookies_body: "Kavioura använder inga spårningscookies. Vi lagrar en session-token och ditt språkval i webbläsarens lokala lagring så att du förblir inloggad och ser webbplatsen på ditt språk. Dessa raderas när du loggar ut eller raderar ditt konto.",
    pp_security_title: "9. Säkerhet",
    pp_security_body: "Lösenord lagras hashade med bcrypt. Åtkomst till ditt konto kräver en signerad session-token. Välj ett starkt lösenord och logga ut på delade enheter.",
    pp_contact_title: "10. Kontakt",
    pp_contact_body: "Dataskyddsförfrågningar och frågor: kaviourapalvelut@gmail.com. Vi svarar inom en arbetsdag.",
    pp_contact_line: "Frågor? Skriv till oss:",
    cookie_title: "En snabb notis",
    cookie_body: "Kavioura använder endast webbläsarens lokala lagring för att hålla dig inloggad och komma ihåg ditt språk. Vi använder inga spårningscookies.",
    cookie_learn_more: "Läs vår integritetspolicy",
    cookie_accept: "Uppfattat",
    danger_zone: "Riskzon",
    delete_account: "Radera konto",
    delete_account_desc: "Radera permanent ditt Kavioura-konto och all associerad data. Denna åtgärd kan inte ångras.",
    delete_account_warning: "Detta raderar ditt konto permanent. Det går inte att återställa senare.",
    delete_bullet_profile: "Din profil, namn, e-post och bild",
    delete_bullet_listings: "Alla dina annonser och uppladdade bilder",
    delete_bullet_messages: "Alla konversationer du haft på Kavioura",
    delete_bullet_reviews: "Recensioner du skrivit och recensioner om dig",
    delete_bullet_bookings: "Bokningshistorik och lediga tider",
    delete_confirm_label: "Skriv DELETE för att bekräfta",
    delete_account_confirm: "Radera mitt konto",
    account_deleted: "Ditt konto har raderats.",
    no_photo: "Ingen bild",
    role_hint: "Detta är bara en etikett så att andra ser hur du använder Kavioura. Det begränsar inte vad du kan publicera eller bläddra bland — vem som helst kan annonsera, köpa, boka eller skicka meddelanden oavsett roll.",
    availability: "Tillgänglighet",
    availability_title: "Kalender",
    availability_sub: "Klicka på valfri dag för att se lediga tider. Bokade tider visas som otillgängliga — detaljerna är privata mellan parterna.",
    availability_sub_owner: "Klicka på en dag och lägg till en tid. Andra ser dem och kan boka direkt. Bokningsdetaljer förblir privata.",
    open: "Ledig",
    open_short: "ledig",
    booked_short: "bokad",
    pending_short: "väntar",
    request_accepted: "Förfrågan accepterad",
    request_declined: "Förfrågan avböjd",
    unknown: "Okänd",
    completion_confirmed: "Bekräftat — du kan nu lämna en recension.",
    mark_deal_done: "Affär klar · Bekräfta",
    write_review: "Skriv en recension",
    you_reviewed: "Du har recenserat",
    review_completion: "Betygsätt personen",
    edit_profile: "Redigera profil",
    settings: "Inställningar",
    account: "Konto",
    profile: "Profil",
    security: "Säkerhet",
    bio: "Beskrivning",
    bio_placeholder: "Berätta lite om dig själv, ditt stall, din gren…",
    security_hint: "Bekräfta ändringar genom att ange ditt nuvarande lösenord.",
    current_password: "Nuvarande lösenord",
    new_email: "Ny e-post",
    new_password: "Nytt lösenord",
    confirm_new_password: "Bekräfta nytt lösenord",
    passwords_no_match: "De nya lösenorden matchar inte.",
    nothing_to_change: "Ange en ny e-post eller ett nytt lösenord.",
    saved: "Sparat.",
    my_profile: "Min profil",
    view_my_profile: "Visa och redigera min profil",
    booking_request: "Bokningsförfrågan",
    booking_requested: "Bokningsförfrågan skickad",
    booking_notes_label: "Meddelande till tjänsteleverantören (valfritt)",
    booking_notes_placeholder: "Något de bör veta — din häst, plats, behov…",
    booking_notes_hint: "Tjänsteleverantören ser detta meddelande. Ditt namn delas först efter godkännande.",
    send_request: "Skicka förfrågan",
    login_required: "Logga in för att boka.",
    my_bookings_sub: "Alla bokningar du är inblandad i — som kund eller som leverantör. Väntande förfrågningar du fått visar acceptera/avböj-knappar.",
    no_bookings_this_day: "Inga bokningar denna dag.",
    as_provider: "Som leverantör",
    as_client: "Som kund",
    cancel_booking_q: "Avboka denna bokning?",
    status_pending: "Väntar",
    status_accepted: "Accepterad",
    status_declined: "Avböjd",
    status_cancelled: "Avbokad",
    boost_intro: "Lyft din annons överst i sök- och kategorisidor.",
    boost_24h_label: "24 timmar",
    boost_7d_label: "7 dagar",
    boost_24h_desc: "En dag med premiumplacering",
    boost_7d_desc: "En hel vecka med premiumplacering",
    proceed_to_payment: "Fortsätt till betalning",
    stripe_powered: "Säker betalning via Stripe",
    payment_checking: "Bekräftar din betalning…",
    payment_checking_sub: "Detta tar oftast några sekunder. Stäng inte sidan.",
    payment_success_title: "Betalning lyckades — Boost aktiv!",
    payment_success_sub: "Din annons är nu boostad och visas överst.",
    view_listing: "Visa annons",
    payment_pending_title: "Betalningen behandlas fortfarande",
    payment_pending_sub: "Det tar längre än vanligt. Din boost aktiveras automatiskt när betalningen är klar.",
    payment_cancelled_title: "Betalning avbruten",
    payment_cancelled_sub: "Inget debiterades. Du kan försöka igen när som helst.",
    back_to_dashboard: "Tillbaka till översikten",
    today: "Idag",
    no_slots_this_day: "Inga lediga tider denna dag. Välj en annan dag eller lägg till en tid nedan.",
    add_slot_on_day: "Lägg till en tid denna dag",
    slot_title_placeholder: "t.ex. Hovslagare, veterinär…",
    confirm_remove_slot: "Ta bort denna tid?",
    time_end_after_start: "Sluttiden måste vara efter starttiden.",
    your_booking: "Din bokning",
    private_details: "Privat",
    wd_mon: "Mån", wd_tue: "Tis", wd_wed: "Ons", wd_thu: "Tor", wd_fri: "Fre", wd_sat: "Lör", wd_sun: "Sön",
    refine_tack: "Filtrera utrustning",
    clear_filters: "Rensa filter",
    any: "Alla",
    size_pony: "Pony",
    size_cob: "Cob",
    size_full: "Full",
    size_xl: "XL",
    size_oversize: "Extra stor",
    size_custom_placeholder: "eller skriv t.ex. 17.5\", 155cm, 135cm…",
    no_listings_match: "Inga annonser matchar dina filter.",
    horse_category: "Kategori",
    hc_pony: "Ponny",
    hc_horse: "Häst",
    hc_foal: "Föl",
    hc_yearling: "Åring",
  },
};

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("kav_lang") || "en");
  useEffect(() => { localStorage.setItem("kav_lang", lang); }, [lang]);
  const t = useCallback(
    (key) => translations[lang]?.[key] ?? translations.en[key] ?? key,
    [lang]
  );
  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}
